import React, { useState, useEffect } from 'react';
import { X, Bot, Mail, Briefcase, MapPin, User, Sparkles, AlertCircle, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import JobMatchCard from './JobMatchCard';
import {
    CandidateForRecommendation,
    JobRecommendation,
    getJobRecommendations,
    getImportedCandidateJobRecommendations,
    sendAsDavid,
    sendRecommendationEmail
} from '../services/recommendationsApi';

interface JobRecommendationModalProps {
    isOpen: boolean;
    onClose: () => void;
    candidate: CandidateForRecommendation | null;
    onSuccess?: () => void;
}

const JobRecommendationModal: React.FC<JobRecommendationModalProps> = ({
    isOpen,
    onClose,
    candidate,
    onSuccess
}) => {
    const [jobs, setJobs] = useState<JobRecommendation[]>([]);
    const [selectedJobs, setSelectedJobs] = useState<JobRecommendation[]>([]);
    const [message, setMessage] = useState('');
    const [isLoadingJobs, setIsLoadingJobs] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Load jobs when modal opens with a candidate
    useEffect(() => {
        if (isOpen && candidate) {
            loadJobRecommendations();
        } else {
            // Reset state when modal closes
            setJobs([]);
            setSelectedJobs([]);
            setMessage('');
            setError(null);
            setSuccessMessage(null);
        }
    }, [isOpen, candidate]);

    // Both 'imported' and 'imported_se' are off-platform — they use the
    // same email-based flow. Only 'platform' uses the David message flow.
    const isImported = candidate?.source === 'imported' || candidate?.source === 'imported_se';
    const importedCollection: 'all_roles' | 'software_engineering' =
        candidate?.source === 'imported_se' ? 'software_engineering' : 'all_roles';

    // Generate default message when candidate changes
    useEffect(() => {
        if (candidate) {
            const firstName = candidate.firstName || 'there';
            if (isImported) {
                setMessage(`Hi ${firstName},\n\nWe came across your profile and thought you'd be a great match for some roles we're helping fill. Take a look at the openings below and let us know which ones interest you — happy to share more details and connect you with the hiring teams.\n\nBest,\nThe Hush Team`);
            } else {
                setMessage(`Hi ${firstName}!\n\nI found some great job opportunities that match your skills and experience. Take a look and let me know if any of these interest you!\n\nBest,\nDavid`);
            }
        }
    }, [candidate, isImported]);

    const loadJobRecommendations = async () => {
        if (!candidate) return;

        setIsLoadingJobs(true);
        setError(null);

        try {
            const response = isImported
                ? await getImportedCandidateJobRecommendations(candidate.id, importedCollection)
                : await getJobRecommendations(candidate.id);
            setJobs(response.matches || []);
            // Auto-select top 3 jobs
            setSelectedJobs((response.matches || []).slice(0, 3));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load job recommendations');
        } finally {
            setIsLoadingJobs(false);
        }
    };

    const handleToggleJob = (job: JobRecommendation) => {
        setSelectedJobs(prev => {
            const isSelected = prev.some(j => j.job_id === job.job_id);
            if (isSelected) {
                return prev.filter(j => j.job_id !== job.job_id);
            } else {
                return [...prev, job];
            }
        });
    };

    const handleSend = async () => {
        if (!candidate || selectedJobs.length === 0 || !message.trim()) return;

        setIsSending(true);
        setError(null);

        try {
            if (isImported) {
                const fullName = `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim() || 'Candidate';
                await sendRecommendationEmail({
                    candidateEmail: candidate.email,
                    candidateName: fullName,
                    jobs: selectedJobs.map(j => ({
                        title: j.title,
                        companyName: j.company_name,
                        location: j.location,
                        matchScore: j.match_score
                    })),
                    message: message.trim()
                });
                setSuccessMessage('Email sent successfully!');
            } else {
                await sendAsDavid({
                    target_user_id: candidate.id,
                    content: message.trim(),
                    job_recommendations: selectedJobs
                });
                setSuccessMessage('Message sent successfully!');
            }

            setTimeout(() => {
                onClose();
                onSuccess?.();
            }, 1500);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to send');
        } finally {
            setIsSending(false);
        }
    };

    const getInitials = (firstName?: string, lastName?: string) => {
        if (firstName && lastName) {
            return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
        }
        if (firstName) {
            return firstName.charAt(0).toUpperCase();
        }
        return '??';
    };

    const fullName = candidate
        ? `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim() || 'Unknown'
        : '';

    const canSend = selectedJobs.length > 0 && message.trim() && !isSending;

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ duration: 0.3, type: 'spring', damping: 25, stiffness: 300 }}
                        className="w-full max-w-6xl bg-white rounded-2xl shadow-2xl overflow-hidden flex max-h-[90vh]"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Left Panel - Candidate Info */}
                        <div className="w-2/5 bg-[#F9F9F9] p-6 overflow-y-auto border-r border-[#EDEDED]">
                            {/* Header with Close Button */}
                            <div className="flex justify-between items-start mb-6">
                                <h2 className="text-lg font-semibold text-[#0B111E]">
                                    Job Recommendations
                                </h2>
                                <button
                                    onClick={onClose}
                                    className="p-1.5 rounded-full hover:bg-white/80 text-[#757575] hover:text-[#0B111E] transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {candidate && (
                                <>
                                    {/* Candidate Avatar and Name */}
                                    <div className="flex items-center gap-4 mb-6">
                                        <div className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl bg-gradient-to-br from-[#015FA6] to-[#0077cc]">
                                            {getInitials(candidate.firstName, candidate.lastName)}
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-[#0B111E]">
                                                {fullName}
                                            </h3>
                                            <p className="text-sm text-[#757575]">
                                                {candidate.email}
                                            </p>
                                            {candidate.linkedinUrl && (
                                                <a
                                                    href={candidate.linkedinUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1 text-xs text-[#015FA6] hover:underline mt-1"
                                                >
                                                    <ExternalLink size={12} />
                                                    LinkedIn Profile
                                                </a>
                                            )}
                                        </div>
                                    </div>

                                    {/* Source Badge */}
                                    {isImported && (
                                        <div className="flex items-center gap-2 mb-4">
                                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                                Imported Candidate
                                            </span>
                                            {candidate.importSource && (
                                                <span className="text-xs text-[#757575]">
                                                    via {candidate.importSource}
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {/* Profile Summary */}
                                    <div className="bg-white rounded-xl p-4 mb-4 border border-[#EDEDED]">
                                        <h4 className="text-xs font-semibold text-[#757575] uppercase tracking-wider mb-3">
                                            Profile Summary
                                        </h4>
                                        <div className="space-y-2">
                                            {candidate.yearsOfExperience && (
                                                <div className="flex items-center gap-2 text-sm text-[#0B111E]">
                                                    <Briefcase size={14} className="text-[#757575]" />
                                                    {candidate.yearsOfExperience}+ years experience
                                                </div>
                                            )}
                                            {candidate.currentTitle && (
                                                <div className="flex items-center gap-2 text-sm text-[#0B111E]">
                                                    <User size={14} className="text-[#757575]" />
                                                    {candidate.currentTitle}
                                                </div>
                                            )}
                                            {candidate.location && (
                                                <div className="flex items-center gap-2 text-sm text-[#0B111E]">
                                                    <MapPin size={14} className="text-[#757575]" />
                                                    {candidate.location}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Skills */}
                                    {candidate.skills && candidate.skills.length > 0 && (
                                        <div className="bg-white rounded-xl p-4 mb-4 border border-[#EDEDED]">
                                            <h4 className="text-xs font-semibold text-[#757575] uppercase tracking-wider mb-3">
                                                Top Skills
                                            </h4>
                                            <div className="flex flex-wrap gap-2">
                                                {candidate.skills.slice(0, 8).map((skill, index) => (
                                                    <span
                                                        key={index}
                                                        className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-[#015FA6] border border-blue-100"
                                                    >
                                                        {skill}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Match Insights */}
                                    <div className="bg-white rounded-xl p-4 border border-[#EDEDED]">
                                        <h4 className="text-xs font-semibold text-[#757575] uppercase tracking-wider mb-3">
                                            Match Insights
                                        </h4>
                                        <div className="space-y-2 text-sm text-[#515151]">
                                            <div className="flex items-center gap-2">
                                                <Sparkles size={14} className="text-[#015FA6]" />
                                                <span>{jobs.length} matching jobs found</span>
                                            </div>
                                            {jobs.length > 0 && (
                                                <div className="flex items-center gap-2">
                                                    <span className="w-3.5 h-3.5 rounded-full bg-green-500" />
                                                    <span>
                                                        Avg match: {Math.round(jobs.reduce((acc, j) => acc + j.match_score, 0) / jobs.length * 100)}%
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Right Panel - Jobs and Message */}
                        <div className="w-3/5 flex flex-col bg-white">
                            {/* Jobs Section */}
                            <div className="flex-1 overflow-y-auto p-6">
                                <h3 className="text-sm font-semibold text-[#0B111E] mb-4">
                                    Select Jobs to Recommend ({selectedJobs.length} selected)
                                </h3>

                                {isLoadingJobs ? (
                                    <div className="flex flex-col items-center justify-center py-12">
                                        <div className="w-10 h-10 border-3 border-[#015FA6]/20 border-t-[#015FA6] rounded-full animate-spin mb-4" />
                                        <p className="text-sm text-[#757575]">Finding matching jobs...</p>
                                    </div>
                                ) : error ? (
                                    <div className="flex flex-col items-center justify-center py-12">
                                        <AlertCircle size={40} className="text-red-400 mb-4" />
                                        <p className="text-sm text-red-600 mb-4">{error}</p>
                                        <button
                                            onClick={loadJobRecommendations}
                                            className="px-4 py-2 bg-[#015FA6] text-white rounded-lg text-sm hover:bg-[#014a85]"
                                        >
                                            Try Again
                                        </button>
                                    </div>
                                ) : jobs.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-12">
                                        <Briefcase size={40} className="text-[#CBCBCB] mb-4" />
                                        <p className="text-sm text-[#757575]">No matching jobs found</p>
                                    </div>
                                ) : (
                                    <motion.div
                                        initial="hidden"
                                        animate="visible"
                                        variants={{
                                            visible: {
                                                transition: {
                                                    staggerChildren: 0.05
                                                }
                                            }
                                        }}
                                        className="space-y-3"
                                    >
                                        {jobs.map(job => (
                                            <motion.div
                                                key={job.job_id}
                                                variants={{
                                                    hidden: { opacity: 0, y: 10 },
                                                    visible: { opacity: 1, y: 0 }
                                                }}
                                            >
                                                <JobMatchCard
                                                    job={job}
                                                    isSelected={selectedJobs.some(j => j.job_id === job.job_id)}
                                                    onToggle={handleToggleJob}
                                                />
                                            </motion.div>
                                        ))}
                                    </motion.div>
                                )}
                            </div>

                            {/* Message Composer */}
                            <div className="border-t border-[#EDEDED] p-6 bg-[#F9F9F9]">
                                <h3 className="text-sm font-semibold text-[#0B111E] mb-3">
                                    Message to Candidate
                                </h3>

                                <textarea
                                    value={message}
                                    onChange={e => setMessage(e.target.value)}
                                    placeholder="Write a personalized message..."
                                    rows={4}
                                    className="w-full px-4 py-3 rounded-xl border border-[#EDEDED] bg-white text-sm text-[#0B111E] placeholder:text-[#A5A5A5] focus:outline-none focus:ring-2 focus:ring-[#015FA6]/20 focus:border-[#015FA6] resize-none"
                                />

                                <div className="flex items-center justify-between mt-4">
                                    <p className="text-xs text-[#757575]">
                                        {message.length} characters
                                    </p>

                                    {successMessage ? (
                                        <div className="flex items-center gap-2 text-green-600 text-sm font-medium">
                                            <motion.div
                                                initial={{ scale: 0 }}
                                                animate={{ scale: 1 }}
                                                className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center"
                                            >
                                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                </svg>
                                            </motion.div>
                                            {successMessage}
                                        </div>
                                    ) : (
                                        <button
                                            onClick={handleSend}
                                            disabled={!canSend}
                                            className="flex items-center gap-2 px-5 py-2.5 bg-[#015FA6] text-white rounded-xl text-sm font-medium hover:bg-[#014a85] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                        >
                                            {isSending ? (
                                                <>
                                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                    Sending...
                                                </>
                                            ) : isImported ? (
                                                <>
                                                    <Mail size={16} />
                                                    Send via Email
                                                </>
                                            ) : (
                                                <>
                                                    <Bot size={16} />
                                                    Send as David
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default JobRecommendationModal;
